import {
  categorySelector,
  filtersSelector,
  isCategoryMapSelector,
  isUnauthorizedSelector,
  isUserDataSelector,
  userDataErrorSelector,
} from "@app-redux/selectors";
import { setPageNumFilter } from "@app-redux/slices/filtersSlice";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { getProfileList } from "@app-redux/thunk/profileListThunk";
import { getUserData } from "@app-redux/thunk/userDataThunk";
import { getRelationshipLevels } from "@app-redux/thunk/relationshipLevelThunk";
import { AppRouter } from "@components/AppRouter";
import { Loader } from "@components/Loader";
import { AppError } from "@pages/AppError";
import { SignedOut } from "@pages/SignedOut";
import { Unauthorized } from "@pages/Unauthorized";
import { ROUTER_PATHS } from "@/constants";
import React, { useEffect } from "react";
import { useAuth } from "./hooks/useAuth";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "@/config/authConfig";
import "./style/index.css";
import { getCategoryFilters } from "./utils";

// comment to test & trigger CI/CD pipeline

let isFirstRender = true;

export default function App() {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(filtersSelector);
  const { map, data: categoryData } = useAppSelector(categorySelector);
  const isUserData = useAppSelector(isUserDataSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);
  const userDataError = useAppSelector(userDataErrorSelector);
  const isCategoryMap = useAppSelector(isCategoryMapSelector);
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useAuth();

  // Trigger login redirect when not authenticated and no interaction in progress
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      if (window.location.pathname === `/${ROUTER_PATHS.signedOut}`) return;
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress]);

  // Only fetch user data once authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isUserData || userDataError) return;
    dispatch(getUserData());
    dispatch(getRelationshipLevels());
  }, [isUserData, isAuthenticated, userDataError]);

  useEffect(() => {
    if (!isCategoryMap) return;
    if (isFirstRender && filters.page) {
      dispatch(setPageNumFilter(0));
    } else {
      const { categories_values, ...rest } = filters;
      const categoryFilters = getCategoryFilters(map, categories_values, categoryData);
      dispatch(getProfileList({ ...rest, ...categoryFilters }));
    }
    isFirstRender = false;
  }, [filters, isCategoryMap]);

  if (!isAuthenticated) {
    if (window.location.pathname === `/${ROUTER_PATHS.signedOut}`) {
      return (
        <div className="App">
          <SignedOut />
        </div>
      );
    }
    return (
      <div className="App">
        <Loader className="content" show={true} />
      </div>
    );
  }

  if (isUnauthorized) {
    return (
      <div className="App">
        <Unauthorized />
      </div>
    );
  }

  if (userDataError && !isUserData) {
    return (
      <div className="App">
        <AppError onRetry={() => dispatch(getUserData())} />
      </div>
    );
  }

  return (
    <div className="App">
      <AppRouter />
    </div>
  );
}
